/**
 * useRoutes Hook
 *
 * @module core/modules/waste/hooks
 *
 * @description
 * Hook para gestión de rutas de recolección.
 */

import { useCallback, useEffect } from 'react';
import { useRoutesStore } from '../store';
import { wasteApi, getWasteApiErrorMessage } from '../services';
import type { WasteRoute, CreateRouteDto, UpdateRouteDto } from '../types';

interface UseRoutesOptions {
  projectId: string;
  autoLoad?: boolean;
}

/**
 * Hook para gestión de rutas
 */
export function useRoutes({ projectId, autoLoad = true }: UseRoutesOptions) {
  const store = useRoutesStore();
  const { routes, isLoading, error, setRoutes, addRoute, updateRoute, removeRoute, setLoading, setError } = store;

  /**
   * Cargar rutas del proyecto
   */
  const loadRoutes = useCallback(async () => {
    if (!projectId) {
      console.log('[useRoutes] No hay projectId, no se cargan rutas');
      return;
    }

    console.log('[🔍 BOOTSTRAP] useRoutes cargando rutas:', {
      projectId,
      timestamp: new Date().toISOString()
    });
    setLoading(true);
    try {
      const data = await wasteApi.routes.getAll({ projectId });
      console.log('[useRoutes] ✅ Rutas cargadas:', data.length);
      console.log('[useRoutes] Rutas:', data.map(r => ({ code: r.code, id: r.id })));
      setRoutes(data);
    } catch (err: any) {
      console.error('[🔍 BOOTSTRAP] useRoutes error cargando rutas:', {
        projectId,
        status: err?.response?.status,
        message: err?.message || String(err),
        timestamp: new Date().toISOString()
      });
      setError(getWasteApiErrorMessage(err));
    }
  }, [projectId, setLoading, setRoutes, setError]);

  /**
   * Crear ruta
   */
  const createRoute = useCallback(
    async (data: CreateRouteDto): Promise<WasteRoute | null> => {
      try {
        const route = await wasteApi.routes.create(projectId, data);
        addRoute(route);
        return route;
      } catch (err) {
        setError(getWasteApiErrorMessage(err));
        return null;
      }
    },
    [projectId, addRoute, setError]
  );

  /**
   * Actualizar ruta
   */
  const editRoute = useCallback(
    async (id: string, data: UpdateRouteDto): Promise<WasteRoute | null> => {
      try {
        const route = await wasteApi.routes.update(id, data);
        updateRoute(id, route);
        return route;
      } catch (err) {
        setError(getWasteApiErrorMessage(err));
        return null;
      }
    },
    [updateRoute, setError]
  );

  /**
   * Eliminar ruta
   */
  const deleteRoute = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await wasteApi.routes.delete(id);
        removeRoute(id);
        return true;
      } catch (err) {
        setError(getWasteApiErrorMessage(err));
        return false;
      }
    },
    [removeRoute, setError]
  );

  /**
   * Optimizar ruta (TSP)
   */
  const optimizeRoute = useCallback(
    async (id: string): Promise<WasteRoute | null> => {
      try {
        const optimized = await wasteApi.routes.optimize(id);
        updateRoute(id, optimized);
        return optimized;
      } catch (err) {
        setError(getWasteApiErrorMessage(err));
        return null;
      }
    },
    [updateRoute, setError]
  );

  /**
   * Obtener ruta por ID
   */
  const getRouteById = useCallback(
    (id: string): WasteRoute | undefined => {
      return routes.find((r) => r.id === id);
    },
    [routes]
  );

  /**
   * Obtener GeoJSON de rutas
   */
  const loadRoutesGeoJSON = useCallback(async () => {
    try {
      return await wasteApi.routes.getGeoJSON(projectId);
    } catch (err) {
      setError(getWasteApiErrorMessage(err));
      return null;
    }
  }, [projectId, setError]);

  // Auto-load al montar
  useEffect(() => {
    console.log('[useRoutes] useEffect auto-load ejecutándose', { autoLoad, projectId });
    if (autoLoad && projectId) {
      console.log('[useRoutes] 🚀 Auto-load activado, cargando rutas...');
      loadRoutes();
    } else {
      console.log('[useRoutes] ⏭️ Auto-load no activado o sin projectId');
    }
  }, [autoLoad, projectId, loadRoutes]);

  // Computed
  const activeRoutes = routes.filter((r) => r.status === 'active');

  return {
    // State
    routes,
    isLoading,
    error,

    // Actions
    loadRoutes,
    createRoute,
    editRoute,
    deleteRoute,
    optimizeRoute,
    getRouteById,
    loadRoutesGeoJSON,

    // Computed
    activeRoutes,
    totalRoutes: routes.length,
  };
}
